import type { Role } from '@vlm/api';
import {
  AppShell,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  Logo,
  PageLoader,
  Rail,
  RailItem,
  RailLogo,
  RoadSign,
  ScreenLoader,
  Sheet,
  Tooltip,
  TopBar,
  toast,
  cn,
  useStoredFlag,
} from '@vlm/ui';
import { ChevronDown, LogOut, Menu, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import { Suspense, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ALERT_SIGN } from '../components/status';
import { useT } from '../i18n';
import { api, fileUrl } from '../lib/api';
import { ADMINS, restoreSession, signOut, useAuth, useCan, useUser } from '../lib/auth';
import { paymentState, useBilling, useCompany } from '../lib/company';
import { useLive, useLiveFeed } from '../lib/live';
import { usePrefs, type Theme } from '../lib/prefs';
import { FleetSearch } from './FleetSearch';
import { visibleNav } from './nav';
import { Notifications } from './Notifications';

/** Signed in, or on the way to the sign-in page. */
export function Layout() {
  const { t } = useT();
  const status = useAuth((s) => s.status);
  const left = useAuth((s) => s.left);
  const location = useLocation();
  if (status === 'checking') return <ScreenLoader label={t('auth.checking')} />;
  if (status === 'unreachable')
    return (
      <div className="grid h-dvh place-items-center bg-canvas p-4">
        <div className="grid justify-items-center gap-4">
          <EmptyState title={t('auth.unreachableTitle')} body={t('auth.unreachableBody')} action={<Button onClick={() => void restoreSession()}>{t('common.retry')}</Button>} />
        </div>
      </div>
    );
  // an expired session comes back to the page it was on; a sign-out does not
  if (status === 'signedOut') return <Navigate to="/login" replace state={left ? null : { from: `${location.pathname}${location.search}` }} />;
  return <Shell />;
}

/** A page only some roles may open; the others see why, not a silent redirect. */
export function RequireRole({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
  const { t } = useT();
  const role = useAuth((s) => s.user?.role);
  if (role && roles.includes(role)) return <>{children}</>;
  return (
    <div className="grid place-items-center px-4 py-16">
      <EmptyState title={t('pages.forbiddenTitle')} body={t('pages.forbiddenBody')} action={<Button asChild variant="secondary"><Link to="/live">{t('pages.goLive')}</Link></Button>} />
    </div>
  );
}

function LiveIndicator() {
  const { t } = useT();
  const state = useLive((s) => s.state);
  const live = state === 'live';
  const label = live ? t('shell.liveOn') : state === 'connecting' ? t('shell.liveConnecting') : state === 'reconnecting' ? t('shell.liveReconnecting') : t('shell.liveOff');
  return (
    <Tooltip label={live ? t('shell.liveOnHint') : t('shell.liveDownHint')}>
      <span
        tabIndex={0}
        className={cn(
          'hidden h-[26px] items-center gap-1.5 rounded-full pe-2.5 ps-2 text-[12px] font-bold sm:inline-flex',
          live ? 'bg-go-tint text-go-ink' : state === 'off' ? 'bg-offline-tint text-offline-ink' : 'bg-caution-tint text-caution-ink',
        )}
      >
        <RoadSign sign={live ? 'go' : state === 'off' ? 'offline' : 'caution'} size={13} />
        {label}
      </span>
    </Tooltip>
  );
}

/**
 * For the company's ADMINs, when a payment needs them: overdue (hazard), or the plan's first
 * payment still awaited (caution). It leads to the Billing page, which has the bank details.
 */
function PaymentChip() {
  const { t, fmt } = useT();
  const billing = useBilling();
  const onTrial = !!useCompany().data?.trial;
  const state = paymentState(billing.data, undefined, onTrial);
  if (!state) return null;
  const overdue = state.kind === 'overdue';
  const amount = fmt.money(state.next.amount, state.next.currency);
  const hint = overdue ? t('shell.paymentOverdueHint', { amount, date: fmt.day(state.next.date) }) : t('shell.paymentFirstHint', { amount });
  return (
    <Tooltip label={hint}>
      <Link
        to="/billing"
        aria-label={`${overdue ? t('shell.paymentOverdue') : t('shell.paymentFirst')}: ${hint}`}
        className={cn(
          'inline-flex h-[26px] items-center gap-1.5 rounded-full pe-2.5 ps-2 text-[12px] font-bold no-underline',
          overdue ? 'bg-hazard-tint text-hazard-ink' : 'bg-caution-tint text-caution-ink',
        )}
      >
        <RoadSign sign={overdue ? 'hazard' : 'caution'} size={13} />
        <span className="hidden sm:inline">{overdue ? t('shell.paymentOverdue') : t('shell.paymentFirst')}</span>
      </Link>
    </Tooltip>
  );
}

/**
 * For the company's ADMINs while a free trial runs: how many days are left (info; caution in the
 * last three). It leads to the Billing page, which says what happens when it ends.
 */
function TrialChip() {
  const { t, fmt } = useT();
  const admin = useCan(ADMINS);
  const company = useCompany().data;
  const trial = company?.trial;
  if (!admin || !trial) return null;
  const soon = trial.daysLeft <= 3;
  const label = t('shell.trialChip', { count: trial.daysLeft });
  const hint = company.plan ? t('shell.trialHintPlan', { date: fmt.day(trial.endsOn) }) : t('shell.trialHint', { date: fmt.day(trial.endsOn) });
  return (
    <Tooltip label={hint}>
      <Link
        to="/billing"
        aria-label={`${label}: ${hint}`}
        className={cn(
          'inline-flex h-[26px] items-center gap-1.5 rounded-full pe-2.5 ps-2 text-[12px] font-bold no-underline',
          soon ? 'bg-caution-tint text-caution-ink' : 'bg-info-tint text-info-ink',
        )}
      >
        <RoadSign sign={soon ? 'caution' : 'info'} size={13} />
        <span className="hidden sm:inline">{label}</span>
      </Link>
    </Tooltip>
  );
}

function LanguageToggle() {
  const { t, lang } = useT();
  const setLang = usePrefs((s) => s.setLang);
  const signedIn = useAuth((s) => s.status === 'signedIn');
  const flip = () => {
    const next = lang === 'ar' ? 'en' : 'ar';
    setLang(next);
    // the user's language follows them to any device (PATCH /api/profile/language); a failure only costs that
    if (signedIn) void api.profile.setLanguage(next).catch(() => {});
  };
  return (
    <Button variant="quiet" size="sm" onClick={flip} aria-label={t('shell.switchLanguageLabel')} lang={lang === 'ar' ? 'en' : 'ar'} className="px-2.5">
      {t('shell.switchLanguage')}
    </Button>
  );
}

const THEMES: Array<{ value: Theme; icon: ReactNode; label: 'shell.themeDay' | 'shell.themeNight' | 'shell.themeAuto' }> = [
  { value: 'day', icon: <Sun size={16} />, label: 'shell.themeDay' },
  { value: 'night', icon: <Moon size={16} />, label: 'shell.themeNight' },
  { value: 'auto', icon: <Monitor size={16} />, label: 'shell.themeAuto' },
];

function UserMenu() {
  const { t, lang } = useT();
  const user = useUser();
  const navigate = useNavigate();
  const theme = usePrefs((s) => s.theme);
  const setTheme = usePrefs((s) => s.setTheme);
  const company = useCompany().data;
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="flex h-control-md items-center gap-2.5 rounded-md pe-1.5 ps-1 text-start transition-colors duration-quick hover:bg-hover" aria-label={t('shell.account')}>
          <Avatar name={user.fullName || user.username} src={fileUrl(user.profileImageUrl, user.updatedAt)} size={30} />
          <span className="hidden min-w-0 leading-tight lg:block">
            <span className="block max-w-[180px] truncate text-body-sm font-bold text-ink">{user.fullName || user.username}</span>
            <span className="block max-w-[180px] truncate text-[12px] text-ink-subtle">{company ? `${t(`roles.${user.role}`)} • ${company.name}` : t(`roles.${user.role}`)}</span>
          </span>
          <ChevronDown size={16} className="hidden text-ink-subtle lg:block" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        <div className="flex items-center gap-3 px-2.5 pb-2 pt-1.5">
          <Avatar name={user.fullName || user.username} src={fileUrl(user.profileImageUrl, user.updatedAt)} size={36} />
          <div className="min-w-0">
            <p className="truncate text-body-sm font-bold text-ink">{user.fullName || user.username}</p>
            <p className="truncate font-mono text-[12px] text-ink-subtle" dir="ltr">
              {user.username}
            </p>
          </div>
        </div>
        {company && (
          <div className="mx-2.5 mb-2 grid gap-0.5 rounded-md bg-surface-sunken px-2.5 py-2">
            <p className="truncate text-body-sm font-bold text-ink">{company.name}</p>
            <p className="truncate text-[12px] text-ink-subtle">
              <span className="font-mono" dir="ltr">
                {company.code}
              </span>
              {company.plan ? ` • ${company.plan.name[lang] || company.plan.name.en}` : company.trial ? ` • ${t('shell.trial')}` : ''}
            </p>
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<UserRound size={16} />} onSelect={() => navigate('/profile')}>
          {t('nav.profile')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('shell.theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          {THEMES.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value} icon={o.icon}>
              {t(o.label)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut size={16} />}
          onSelect={() => {
            signOut();
            // to a clean sign-in page: the next person should not land on this one's last page
            navigate('/login', { replace: true });
          }}
        >
          {t('shell.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNav({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useT();
  const role = useAuth((s) => s.user?.role);
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={<Logo height={30} />} closeLabel={t('common.close')} width={300}>
      <nav aria-label={t('app.navigation')} className="grid gap-1 p-3">
        {visibleNav(role).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => onOpenChange(false)}
            className={({ isActive }) =>
              cn(
                'relative flex h-11 items-center gap-3 rounded-md px-3 text-body font-semibold no-underline',
                isActive ? 'bg-selected text-selected-ink before:absolute before:inset-y-1.5 before:start-0 before:w-[3px] before:rounded-full before:bg-selected-line' : 'text-ink hover:bg-hover',
              )
            }
          >
            <item.icon size={20} aria-hidden="true" />
            {t(item.label)}
          </NavLink>
        ))}
      </nav>
    </Sheet>
  );
}

function Shell() {
  const { t } = useT();
  const navigate = useNavigate();
  const role = useAuth((s) => s.user?.role);
  const location = useLocation();
  const [menu, setMenu] = useState(false);
  // icons with their names, or icons alone: the reader's choice, kept on this device
  const [railOpen, setRailOpen] = useStoredFlag('vlm.rail.expanded', true);
  const announced = useRef(new Map<string, number>());

  // The API now raises one alert per speeding episode (the cooldown in Alert settings); a cooldown of 0
  // — or the Java API — still alerts on every point over the limit. Either way, announce a vehicle's
  // alert type at most once in two minutes (the bell and the alerts list still count every one).
  useLiveFeed((alert) => {
    const key = `${alert.alertType}|${alert.vehicleName}`;
    const now = Date.now();
    if (now - (announced.current.get(key) ?? 0) < 120_000) return;
    announced.current.set(key, now);
    toast({
      message: t('live.newAlert', { type: t(`alertTypes.${alert.alertType}`), vehicle: alert.vehicleName }),
      sign: ALERT_SIGN[alert.alertType],
      action: { label: t('common.view'), onClick: () => navigate(`/alerts?alert=${alert.alertId}`) },
    });
  });

  const rail = (
    <Rail
      label={t('app.navigation')}
      expanded={railOpen}
      onExpandedChange={setRailOpen}
      expandLabel={t('shell.expandMenu')}
      collapseLabel={t('shell.collapseMenu')}
      logo={
        <Link to="/live" aria-label={t('nav.live')}>
          <RailLogo />
        </Link>
      }
    >
      {visibleNav(role).map((item) => (
        <RailItem key={item.to} asChild label={t(item.label)} icon={<item.icon size={20} aria-hidden="true" />} active={location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)}>
          <NavLink to={item.to} />
        </RailItem>
      ))}
    </Rail>
  );

  const topBar = (
    <TopBar>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-tooltip focus:rounded-md focus:bg-surface focus:px-3 focus:py-2">
        {t('app.skip')}
      </a>
      <IconButton className="md:hidden" label={t('app.menu')} icon={<Menu size={20} aria-hidden="true" />} onClick={() => setMenu(true)} />
      <FleetSearch />
      <div className="ms-auto flex items-center gap-1 sm:gap-2">
        <TrialChip />
        <PaymentChip />
        <LiveIndicator />
        <LanguageToggle />
        <Notifications />
        <UserMenu />
      </div>
    </TopBar>
  );

  return (
    <AppShell rail={rail} topBar={topBar}>
      <MobileNav open={menu} onOpenChange={setMenu} />
      <Suspense fallback={<PageLoader label={t('common.loading')} />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}
